import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { listingsApi, type ListingDetailDto } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, FormField, Input, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";

/**
 * D20 — the listing broker's web contact form, edited from the Listing agent
 * card. A fact about the LISTING (an inquiry form belongs to the listing page),
 * so it is one narrow `PATCH /listings/{id}` and nothing else.
 *
 * <p>The server accepts only an absolute http(s) address and treats an empty
 * value as clear; the card renders it through <code>safeHref</code>.</p>
 */
export function ListingContactFormModal({
  listing,
  open,
  onClose,
}: {
  listing: ListingDetailDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(listing.contactFormUrl ?? "");
  const [error, setError] = useState<string | null>(null);

  // Reset on OPENING, never on a change of `listing`, like the agent modal.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValue(listing.contactFormUrl ?? "");
      setError(null);
    }
  }

  const unchanged = value.trim() === (listing.contactFormUrl ?? "").trim();

  const save = useMutation({
    mutationFn: () => listingsApi.update(listing.id, { contactFormUrl: value.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      // The Broker intro tab's web-form card and readiness strip read this.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(value.trim() ? "Contact form saved." : "Contact form removed.");
      onClose();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  return (
    <Modal
      open={open}
      onClose={() => (save.isPending ? undefined : onClose())}
      title="Listing contact form"
      description="The web page where the listing broker takes inquiries about this listing."
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={save.isPending || unchanged}
            data-testid="listing-contact-form-save"
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <FormField label="Contact form address">
          <Input
            type="url"
            value={value}
            placeholder="https://"
            disabled={save.isPending}
            onChange={(e) => setValue(e.target.value)}
            data-testid="listing-contact-form-input"
          />
        </FormField>
        <p className="text-caption text-gray-mid m-0">
          Leave it empty to remove the contact form from this listing.
        </p>
        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
